import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';

import {
  CSVFileHeaders,
  CSVHeaderMap,
  CSVParseResult,
  downloadCSV,
  downloadTemplate,
  exportFailedRows,
  MAX_FILE_SIZE_IN_MB,
  parseCSV,
  readCSVHeaders,
  validateFileSize,
} from '~utils/csv/csvProcessor';

import { EntityCSVConfig, ImportProgress, ImportResult } from './configs/types';

const BATCH_SIZE = 10;

// The API declares field and message as required on every ModelError_Item, but a
// partial envelope still has to render, so each item is read as a Partial.
type ImportErrorResponse = {
  error?: { errors?: Array<Partial<ModelError_Item>> };
};

/**
 * Prefer the API's structured validation errors ({ error: { errors: [{ model, field, message }] } })
 * over axios's generic "Request failed with status code 422" so the user learns which
 * column failed and why. Fields are translated to CSV column names when the config maps them.
 * A related model reports its own attribute names, so keys are `Model/field` — a `name` error
 * from an Account must not resolve to the project's name column.
 */
const extractImportErrorMessage = (
  error: unknown,
  apiFieldToColumn?: Record<string, string>,
): string => {
  const apiErrors = isAxiosError<ImportErrorResponse>(error)
    ? error.response?.data?.error?.errors
    : undefined;
  if (apiErrors?.length) {
    return apiErrors
      .map(({ model, field, message }) => {
        const key = field && model ? `${model}/${field}` : field;
        const column = key ? apiFieldToColumn?.[key] : undefined;
        if (column && message) return `${column}: ${message}`;
        return message ?? field ?? 'Unknown error';
      })
      .join('; ');
  }
  return error instanceof Error ? error.message : 'Unknown error';
};

const assertFileSize = (file: File) => {
  if (!validateFileSize(file)) {
    throw new Error(t('csv_import_export.file_too_large', { max: MAX_FILE_SIZE_IN_MB }));
  }
};

interface UseCSVImportExportOptions<T, TExport = T> {
  config: EntityCSVConfig<T, TExport>;
  companyId: string;
  loadConfig?: () => Promise<EntityCSVConfig<T, TExport>>;
  onSuccess?: (result: ImportResult<T>) => void;
  onError?: (error: Error) => void;
}

interface UseCSVImportExportReturn<T, TExport> {
  // State
  fileSelectionId: number;
  importConfig: EntityCSVConfig<T, TExport>;
  isLoading: boolean;
  isExporting: boolean;
  isImporting: boolean;
  progress: ImportProgress | null;
  parseResult: CSVParseResult | null;
  importResult: ImportResult<T> | null;
  selectedFileName?: string;

  // Actions
  downloadTemplate: () => void;
  exportAll: () => Promise<void>;
  parseFile: (file: File, columnMapping?: CSVHeaderMap) => Promise<CSVParseResult | null>;
  readFileHeaders: (file: File) => Promise<CSVFileHeaders | null>;
  importData: (validRows: Record<string, string>[]) => Promise<ImportResult<T>>;
  downloadFailedRows: () => void;
  reset: () => void;
}

// The id also keys the uploader, which ignores a re-pick of the file it already holds.
interface FileSelection {
  fileName?: string;
  id: number;
  isParsing: boolean;
}

export function useCSVImportExport<T, TExport = T>({
  config,
  companyId,
  loadConfig,
  onSuccess,
  onError,
}: UseCSVImportExportOptions<T, TExport>): UseCSVImportExportReturn<T, TExport> {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [fileSelection, setFileSelection] = useState<FileSelection>({
    id: 0,
    isParsing: false,
  });
  const [parsedFile, setParsedFile] = useState<{
    config: EntityCSVConfig<T, TExport>;
    result: CSVParseResult;
  } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult<T> | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const fileSelectionIdRef = useRef(0);

  const parseResult = parsedFile?.result ?? null;
  const importConfig = parsedFile?.config ?? config;

  const resetFileSelection = useCallback((file?: File) => {
    fileSelectionIdRef.current += 1;
    setFileSelection({
      fileName: file?.name,
      id: fileSelectionIdRef.current,
      isParsing: Boolean(file),
    });
    setParsedFile(null);

    return fileSelectionIdRef.current;
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    downloadTemplate(config.columns, config.entityType);
  }, [config.columns, config.entityType]);

  const handleExportAll = useCallback(async () => {
    setIsExporting(true);

    try {
      const { columns, entityType, fetchAll, transformExport } =
        loadConfig && config.shouldLoadConfigBeforeExport ? await loadConfig() : config;

      if (!fetchAll || !transformExport) {
        return;
      }

      const entities = await fetchAll(companyId);
      const exportData = entities.map(transformExport);
      const filename = `${entityType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(exportData, filename, columns);
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setIsExporting(false);
    }
  }, [config, companyId, loadConfig, onError]);

  const handleReadFileHeaders = useCallback(
    async (file: File) => {
      const selectionId = resetFileSelection(file);
      const isCurrentSelection = () => selectionId === fileSelectionIdRef.current;

      try {
        assertFileSize(file);

        const fileHeaders = await readCSVHeaders(file);
        if (!isCurrentSelection()) return null;

        setFileSelection((selection) => ({ ...selection, isParsing: false }));
        return fileHeaders;
      } catch (error) {
        if (isCurrentSelection()) {
          onError?.(error as Error);
          resetFileSelection();
        }

        return null;
      }
    },
    [onError, resetFileSelection],
  );

  const handleParseFile = useCallback(
    async (file: File, columnMapping?: CSVHeaderMap) => {
      const selectionId = resetFileSelection(file);
      const isCurrentSelection = () => selectionId === fileSelectionIdRef.current;

      try {
        assertFileSize(file);

        const parseConfig = loadConfig ? await loadConfig() : config;
        const result = await parseCSV(file, parseConfig.columns, columnMapping);

        // Apply custom validation if provided, merging with built-in validation
        if (parseConfig.validateRow) {
          // Build a map of existing errors from built-in validation (by row index)
          const builtInErrorsByRow = new Map<
            number,
            { row: number; field: string; message: string }[]
          >();
          for (const invalidRow of result.invalidRows) {
            for (const error of invalidRow.errors) {
              const existing = builtInErrorsByRow.get(error.row) || [];
              existing.push(error);
              builtInErrorsByRow.set(error.row, existing);
            }
          }

          const newValidRows: Record<string, string>[] = [];
          const newInvalidRows: {
            row: Record<string, string>;
            errors: { row: number; field: string; message: string }[];
          }[] = [];
          const newErrors: { row: number; field: string; message: string }[] = [];

          for (let i = 0; i < result.data.length; i++) {
            const row = result.data[i];
            const rowIndex = i + 2; // +2 for 0-index and header

            // Get built-in validation errors for this row
            const builtInErrors = builtInErrorsByRow.get(rowIndex) || [];

            // Run custom validation
            const customErrors = parseConfig.validateRow(row);
            const formattedCustomErrors = customErrors.map((message) => ({
              row: rowIndex,
              field: '',
              message,
            }));

            // Combine both sets of errors. Built-in and config validation often flag the
            // same problem (e.g. a blank required column), so drop message duplicates.
            const allErrors = [...builtInErrors, ...formattedCustomErrors].filter(
              (error, index, list) =>
                list.findIndex((other) => other.message === error.message) === index,
            );

            if (allErrors.length > 0) {
              newErrors.push(...allErrors);
              newInvalidRows.push({ row, errors: allErrors });
            } else {
              newValidRows.push(row);
            }
          }

          result.validRows = newValidRows;
          result.invalidRows = newInvalidRows;
          result.errors = newErrors;
        }

        if (!isCurrentSelection()) {
          return null;
        }

        setParsedFile({ config: parseConfig, result });
        setFileSelection((selection) => ({ ...selection, isParsing: false }));
        return result;
      } catch (error) {
        if (isCurrentSelection()) {
          onError?.(error as Error);
          resetFileSelection();
        }

        return null;
      }
    },
    [config, loadConfig, onError, resetFileSelection],
  );

  const handleImportData = useCallback(
    async (validRows: Record<string, string>[]): Promise<ImportResult<T>> => {
      setIsImporting(true);

      const result: ImportResult<T> = {
        succeeded: [],
        failed: [],
      };

      setProgress({
        total: validRows.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
      });

      // Own the controller locally: the ref is shared, so a previous overlapping import
      // clearing it must not read as this call being cancelled, and vice versa.
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // Process in batches
        const batchSize = importConfig.importBatchSize ?? BATCH_SIZE;
        for (let i = 0; i < validRows.length; i += batchSize) {
          if (controller.signal.aborted) {
            break;
          }

          const batch = validRows.slice(i, i + batchSize);
          const batchPromises = batch.map(async (row) => {
            try {
              const hasId = row.id?.trim();
              let entity: T;

              if (hasId && importConfig.update) {
                entity = await importConfig.update(row.id, row, companyId);
              } else {
                entity = await importConfig.create(companyId, row);
              }

              result.succeeded.push(entity);
              return { success: true, row };
            } catch (error) {
              const errorMessage = extractImportErrorMessage(
                error,
                importConfig.apiFieldToColumn,
              );
              result.failed.push({ row, error: errorMessage });
              return { success: false, row, error: errorMessage };
            }
          });

          const batchResults = await Promise.allSettled(batchPromises);

          // Update progress after each batch
          const completedCount = i + batchResults.length;
          setProgress({
            total: validRows.length,
            completed: Math.min(completedCount, validRows.length),
            succeeded: result.succeeded.length,
            failed: result.failed.length,
          });
        }

        // Checked after the loop so a cancel during the final batch counts too. A
        // cancelled import is neither a success nor a failure: reporting it would toast
        // "completed", refresh the caller, and restore the state reset() cleared.
        if (controller.signal.aborted) {
          return result;
        }

        setImportResult(result);
        onSuccess?.(result);
        return result;
      } catch (error) {
        onError?.(error as Error);
        return result;
      } finally {
        setIsImporting(false);
        // Only retire our own controller; a later import may already own the ref.
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [importConfig, companyId, onSuccess, onError],
  );

  const handleDownloadFailedRows = useCallback(() => {
    if (!importResult?.failed.length && !parseResult?.invalidRows.length) {
      return;
    }

    // Combine import failures and parse failures
    const failedRows = [
      ...(importResult?.failed.map(({ row, error }) => ({
        row,
        errors: [{ row: 0, field: '', message: error }],
      })) || []),
      ...(parseResult?.invalidRows || []),
    ];

    if (failedRows.length > 0) {
      exportFailedRows(failedRows, importConfig.columns, importConfig.entityType);
    }
  }, [importResult, parseResult, importConfig]);

  const handleReset = useCallback(() => {
    resetFileSelection();
    setProgress(null);
    setImportResult(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [resetFileSelection]);

  return {
    // State
    fileSelectionId: fileSelection.id,
    importConfig,
    isLoading: isExporting || isImporting || fileSelection.isParsing,
    isExporting,
    isImporting,
    progress,
    parseResult,
    importResult,
    selectedFileName: fileSelection.fileName,

    // Actions
    downloadTemplate: handleDownloadTemplate,
    exportAll: handleExportAll,
    parseFile: handleParseFile,
    readFileHeaders: handleReadFileHeaders,
    importData: handleImportData,
    downloadFailedRows: handleDownloadFailedRows,
    reset: handleReset,
  };
}
